/**
 * 找回密码页：三步流
 * 1) 填邮箱 → sendResetCode 发送 6 位验证码（60s 倒计时重发）
 * 2) 填验证码 → verifyResetCode 换取一次性 reset_token
 * 3) 填新密码 → resetPassword 完成，提示后回「我的」页登录
 * 错误直显服务端 msg（验证码错误 / 过期 / 重置 token 失效等）
 */
import { useEffect, useState } from 'react';
import { sendResetCode, verifyResetCode, resetPassword } from '@shared/api/userCases';

type Step = 1 | 2 | 3;

export default function ResetPasswordPage() {
  const [step, setStep] = useState<Step>(1);
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [cooldown, setCooldown] = useState(0);

  const inputCls = 'w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-500';

  // 重发倒计时
  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  async function onSendCode() {
    const e = email.trim();
    if (!e || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) {
      setOk(null);
      setMsg('请输入正确的邮箱');
      return;
    }
    setBusy(true);
    setMsg(null);
    setOk(null);
    try {
      await sendResetCode(e);
      setStep(2);
      setCooldown(60);
      setOk(`验证码已发送到 ${e}，请查收邮箱（10 分钟内有效）。`);
    } catch (err) {
      setMsg(err instanceof Error ? err.message : '发送失败，请稍后重试');
    } finally {
      setBusy(false);
    }
  }

  async function onVerify() {
    const c = code.trim();
    if (c.length < 4) {
      setOk(null);
      setMsg('请输入邮箱收到的验证码');
      return;
    }
    setBusy(true);
    setMsg(null);
    setOk(null);
    try {
      const token = await verifyResetCode(email.trim(), c);
      setResetToken(token);
      setStep(3);
    } catch (err) {
      setMsg(err instanceof Error ? err.message : '验证码无效或已过期');
    } finally {
      setBusy(false);
    }
  }

  async function onReset() {
    if (password.length < 6) {
      setOk(null);
      setMsg('密码至少 6 位');
      return;
    }
    if (password !== confirm) {
      setOk(null);
      setMsg('两次输入的密码不一致');
      return;
    }
    setBusy(true);
    setMsg(null);
    setOk(null);
    try {
      await resetPassword(resetToken, password, confirm);
      setOk('密码重置成功，请返回登录。');
      setStep(1);
      setCode('');
      setPassword('');
      setConfirm('');
      setResetToken('');
    } catch (err) {
      setMsg(err instanceof Error ? err.message : '重置失败，请稍后重试');
    } finally {
      setBusy(false);
    }
  }

  const btnCls =
    'rounded-md bg-slate-800 px-3 py-2 text-sm font-medium text-white hover:bg-slate-700 disabled:opacity-40';

  return (
    <div className="flex flex-col gap-3">
      <a href="#/mine" className="-mx-2 w-fit rounded px-2 py-1 text-sm text-slate-500 hover:bg-slate-200">
        ← 返回
      </a>
      <div className="rounded-lg bg-white px-4 py-3 shadow">
        <div className="font-medium text-slate-800">重置密码</div>
        <div className="mt-3 flex flex-col gap-2">
          {/* 邮箱：三种步骤都需要（步骤 2/3 只读展示） */}
          <input
            type="email"
            placeholder="邮箱"
            value={email}
            readOnly={step !== 1}
            onChange={(e) => setEmail(e.target.value)}
            className={`${inputCls} ${step !== 1 ? 'bg-slate-50 text-slate-400' : ''}`}
          />

          {step === 1 && (
            <button type="button" onClick={() => void onSendCode()} disabled={busy} className={btnCls}>
              {busy ? '发送中…' : '发送验证码'}
            </button>
          )}

          {step === 2 && (
            <>
              <input
                type="text"
                inputMode="numeric"
                placeholder="邮箱收到的验证码"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                className={inputCls}
              />
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => void onVerify()} disabled={busy} className={btnCls}>
                  {busy ? '校验中…' : '下一步'}
                </button>
                <button
                  type="button"
                  onClick={() => void onSendCode()}
                  disabled={busy || cooldown > 0}
                  className="px-2 py-1 text-xs text-slate-500 hover:text-slate-900 disabled:opacity-40"
                >
                  {cooldown > 0 ? `重新发送(${cooldown}s)` : '重新发送'}
                </button>
              </div>
            </>
          )}

          {step === 3 && (
            <>
              <input
                type="password"
                placeholder="新密码（至少 6 位）"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={inputCls}
              />
              <input
                type="password"
                placeholder="确认新密码"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className={inputCls}
              />
              <button type="button" onClick={() => void onReset()} disabled={busy} className={btnCls}>
                {busy ? '提交中…' : '重置密码'}
              </button>
            </>
          )}

          <div className="text-xs text-slate-400">
            想起来了？
            <a href="#/mine" className="ml-1 text-slate-600 hover:text-slate-900">
              去登录
            </a>
          </div>
        </div>
        {msg && <div className="mt-2 text-xs text-red-500">{msg}</div>}
        {ok && <div className="mt-2 text-xs text-emerald-600">{ok}</div>}
      </div>
    </div>
  );
}
